import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Box, Button, Text } from 'folds';
import { CaretDown, CaretUp, menuIcon } from '$components/icons/phosphor';
import { SequenceCard, SequenceCardStyle } from '$components/sequence-card';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { getClientSyncDiagnostics } from '$client/initMatrix';
import type { Room } from '$types/matrix-sdk';
import { Direction, EventType, NotificationCountType, KnownMembership } from '$types/matrix-sdk';

import { getUnreadInfo, isNotificationEvent } from '$utils/room/unread';
import { allRoomsAtom } from '$state/room-list/roomList';
import { allInvitesAtom } from '$state/room-list/inviteList';
import { getMediaLoadFailureCounts } from '$utils/mediaLoadDiagnostics';

type RoomRenderingDiagnostics = {
  totalRooms: number;
  joinedRooms: number;
  inviteRooms: number;
  roomsMissingName: number;
  roomsMissingAvatar: number;
  roomsWithoutLiveEvents: number;
  roomsWithBackPagination: number;
};

type UnreadDriftRoom = {
  roomId: string;
  roomName: string;
  sdkTotal: number;
  sdkHighlight: number;
  latestNotificationEventId: string | null;
  readUpToEventId: string | null;
  fullyReadEventId: string | null;
};

const getRoomRenderingDiagnostics = (rooms: Room[]): RoomRenderingDiagnostics => {
  let joinedRooms = 0;
  let inviteRooms = 0;
  let roomsMissingName = 0;
  let roomsMissingAvatar = 0;
  let roomsWithoutLiveEvents = 0;
  let roomsWithBackPagination = 0;

  rooms.forEach((room) => {
    const membership = room.getMyMembership();
    if (membership === (KnownMembership.Join as string)) joinedRooms += 1;
    if (membership === (KnownMembership.Invite as string)) inviteRooms += 1;

    if (!room.name || room.name.trim().length === 0) roomsMissingName += 1;

    const roomAvatar = room.getMxcAvatarUrl();
    const fallbackAvatar = room.getAvatarFallbackMember()?.getMxcAvatarUrl();
    if (!roomAvatar && !fallbackAvatar) roomsMissingAvatar += 1;

    const liveTimeline = room.getLiveTimeline();
    if (liveTimeline.getEvents().length === 0) roomsWithoutLiveEvents += 1;
    if (liveTimeline.getPaginationToken(Direction.Backward)) roomsWithBackPagination += 1;
  });

  return {
    totalRooms: rooms.length,
    joinedRooms,
    inviteRooms,
    roomsMissingName,
    roomsMissingAvatar,
    roomsWithoutLiveEvents,
    roomsWithBackPagination,
  };
};

const getUnreadDriftRooms = (mx: ReturnType<typeof useMatrixClient>): UnreadDriftRoom[] => {
  const userId = mx.getUserId();
  if (!userId) return [];

  return mx
    .getRooms()
    .filter(
      (room) => !room.isSpaceRoom() && room.getMyMembership() === (KnownMembership.Join as string)
    )
    .reduce<UnreadDriftRoom[]>((driftRooms, room) => {
      const reconciledUnread = getUnreadInfo(room);
      const sdkTotal = room.getUnreadNotificationCount(NotificationCountType.Total);
      const sdkHighlight = room.getUnreadNotificationCount(NotificationCountType.Highlight);
      if (sdkTotal <= 0 && sdkHighlight <= 0) return driftRooms;
      if (reconciledUnread.total <= 0 && reconciledUnread.highlight <= 0) return driftRooms;

      const latestNotificationEvent = [...room.getLiveTimeline().getEvents()]
        .toReversed()
        .find((event) => !event.isSending() && isNotificationEvent(event));
      const latestNotificationEventId = latestNotificationEvent?.getId() ?? null;
      if (!latestNotificationEventId) return driftRooms;

      const hasReadLatestNotification = room.hasUserReadEvent(userId, latestNotificationEventId);
      if (!hasReadLatestNotification || sdkHighlight > 0) return driftRooms;

      const readUpToEventId = room.getEventReadUpTo(userId) ?? null;
      const fullyReadEventId =
        room.getAccountData(EventType.FullyRead)?.getContent<{ event_id?: string }>()?.event_id ??
        null;

      driftRooms.push({
        roomId: room.roomId,
        roomName: room.name || room.roomId,
        sdkTotal,
        sdkHighlight,
        latestNotificationEventId,
        readUpToEventId,
        fullyReadEventId,
      });
      return driftRooms;
    }, []);
};

const formatListCoverage = (knownCount: number, rangeEnd: number): string => {
  if (knownCount <= 0) return '0/0';
  const loadedCount = Math.max(0, Math.min(knownCount, rangeEnd + 1));
  return `${loadedCount}/${knownCount}`;
};

export function SyncDiagnostics() {
  const mx = useMatrixClient();
  const listedJoinedRoomIds = useAtomValue(allRoomsAtom);
  const listedInviteRoomIds = useAtomValue(allInvitesAtom);
  const [, setTick] = useState(0);
  const [expandSliding, setExpandSliding] = useState(false);

  useEffect(() => {
    const intervalId = window.setInterval(() => setTick((v) => v + 1), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const diagnostics = getClientSyncDiagnostics(mx);
  const roomDiagnostics = getRoomRenderingDiagnostics(mx.getRooms());
  const unreadDriftRooms = getUnreadDriftRooms(mx);
  const listedJoinedRoomIdSet = new Set(listedJoinedRoomIds);
  const sdkRoomsMissingFromList = mx
    .getRooms()
    .filter(
      (room) =>
        room.getMyMembership() === (KnownMembership.Join as string) &&
        !listedJoinedRoomIdSet.has(room.roomId)
    );
  const mediaLoadFailures = [...getMediaLoadFailureCounts()];

  return (
    <Box direction="Column" gap="100">
      <Text size="L400">Sync Diagnostics</Text>
      <SequenceCard
        className={SequenceCardStyle}
        variant="SurfaceVariant"
        direction="Column"
        gap="100"
      >
        <Box direction="Column" gap="100" style={{ padding: '12px' }}>
          <Text size="T300">Transport: {diagnostics.transport}</Text>
          <Text size="T300">State: {diagnostics.syncState ?? 'null'}</Text>
          <Text size="T300">
            Room counts: {roomDiagnostics.totalRooms} total, {roomDiagnostics.joinedRooms} joined,{' '}
            {roomDiagnostics.inviteRooms} invites
          </Text>
          <Text size="T300">
            Room-list atoms: {listedJoinedRoomIds.length} joined, {listedInviteRoomIds.length}{' '}
            invites
          </Text>
          <Text size="T300">
            SDK joined rooms missing from room list: {sdkRoomsMissingFromList.length}
          </Text>
          {sdkRoomsMissingFromList.slice(0, 10).map((room) => (
            <Text key={room.roomId} size="T200" priority="300">
              {room.name || room.roomId} ({room.roomId})
            </Text>
          ))}
          <Text size="T300">
            Media load failures this session:{' '}
            {mediaLoadFailures.length === 0
              ? 'none'
              : mediaLoadFailures.map(([kind, count]) => `${kind}: ${count}`).join(', ')}
          </Text>
          <Text size="T300">Rooms missing name: {roomDiagnostics.roomsMissingName}</Text>
          <Text size="T300">Rooms missing avatar: {roomDiagnostics.roomsMissingAvatar}</Text>
          <Text size="T300">
            Rooms without live events: {roomDiagnostics.roomsWithoutLiveEvents}
          </Text>
          <Text size="T300">
            Rooms with additional history available: {roomDiagnostics.roomsWithBackPagination}
          </Text>
          <Text size="T300">Unread drift rooms: {unreadDriftRooms.length}</Text>
          {unreadDriftRooms.slice(0, 10).map((room) => (
            <Text key={room.roomId} size="T200" priority="300">
              {room.roomName}: sdk {room.sdkTotal}/{room.sdkHighlight} | latest{' '}
              {room.latestNotificationEventId ?? 'null'} | readUpTo {room.readUpToEventId ?? 'null'}{' '}
              | fullyRead {room.fullyReadEventId ?? 'null'}
            </Text>
          ))}

          {diagnostics.sliding && (
            <Box direction="Column" gap="100">
              <Box justifyContent="SpaceBetween" alignItems="Center">
                <Text size="T300">Sliding Sync</Text>
                <Button
                  size="300"
                  variant="Secondary"
                  fill="Soft"
                  outlined
                  radii="300"
                  before={menuIcon(expandSliding ? CaretUp : CaretDown)}
                  onClick={() => setExpandSliding((v) => !v)}
                >
                  <Text size="B300">{expandSliding ? 'Collapse' : 'Expand'}</Text>
                </Button>
              </Box>
              {expandSliding && (
                <Box direction="Column" gap="100">
                  <Text size="T300">Sliding sync base URL: {diagnostics.sliding.baseUrl}</Text>
                  <Text size="T300">Room timeline limit: {diagnostics.sliding.timelineLimit}</Text>
                  {diagnostics.sliding.lists.map((list) => (
                    <Text size="T300" key={list.key}>
                      List `{list.key}` coverage:{' '}
                      {formatListCoverage(list.knownCount, list.rangeEnd)}
                      {list.requestedRangeEnd > list.rangeEnd
                        ? ` (requested through ${list.requestedRangeEnd + 1})`
                        : ''}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </SequenceCard>
    </Box>
  );
}
